import { useMemo, useState } from 'react';
import { FileText, Filter } from 'lucide-react';
import type { RobotEvent } from '@fabricos/protocol-ts';
import { eventSummary, fmtClock } from '@/lib/events';
import { useFleet } from '@/store/fleet';
import { cn } from '@/lib/utils';

type SevFilter = 'all' | 'warning' | 'critical';

/**
 * Vehicle log — the autopilot's own narration (STATUSTEXT: pre-arm reasons,
 * calibration chatter, EKF complaints) plus decoded state-transition events,
 * newest first. This is the live session log; onboard dataflash (.bin)
 * download needs MAVLink LOG_REQUEST support in the Hub and is tracked, not
 * faked, below.
 */
export function VehicleLog({ robotId }: { robotId: string }) {
  const events = useFleet((s) => s.events);
  const [filter, setFilter] = useState<SevFilter>('all');

  const list = useMemo(
    () =>
      events.filter(
        (e) =>
          e.robot_id === robotId &&
          e.kind !== 'param_value' &&
          (filter === 'all' ||
            (filter === 'warning' && e.severity !== 'info') ||
            (filter === 'critical' && e.severity === 'critical')),
      ),
    [events, robotId, filter],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-none items-center gap-2 border-b border-border-default bg-surface-1 px-3 py-2">
        <FileText className="h-3.5 w-3.5 text-accent" />
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-fg-primary">
          Vehicle log
        </span>
        <span className="text-[10.5px] text-fg-muted">{list.length} entries this session</span>
        <span className="ml-auto flex items-center gap-1">
          <Filter className="h-3 w-3 text-fg-muted" />
          {(['all', 'warning', 'critical'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                'rounded border px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wider',
                filter === f
                  ? 'border-accent bg-accent/15 text-accent'
                  : 'border-border-default bg-surface-2 text-fg-secondary hover:bg-surface-3',
              )}
            >
              {f === 'warning' ? 'warn+' : f}
            </button>
          ))}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {list.length === 0 ? (
          <p className="px-4 py-8 text-center text-[12px] text-fg-muted">
            Nothing logged yet this session — pre-arm reasons, calibration chatter and mode changes
            land here as they happen.
          </p>
        ) : (
          <table className="w-full font-mono text-[11px]">
            <tbody>
              {list.map((e, i) => (
                <LogRow key={`${e.ts_ms}-${e.kind}-${i}`} event={e} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex-none border-t border-border-subtle bg-surface-1 px-3 py-1.5 text-[10px] text-fg-muted">
        Onboard dataflash (.bin) download requires MAVLink <code>LOG_REQUEST</code> support in the
        Hub — planned, not yet implemented.
      </div>
    </div>
  );
}

function LogRow({ event }: { event: RobotEvent }) {
  return (
    <tr className="border-b border-border-subtle align-top hover:bg-surface-2/50">
      <td className="w-20 whitespace-nowrap px-3 py-1 tabular-nums text-fg-muted">
        {fmtClock(event.ts_ms)}
      </td>
      <td className="w-28 px-2 py-1">
        <span
          className={cn(
            'rounded px-1.5 py-px text-[9px] uppercase tracking-wider',
            event.severity === 'critical' && 'bg-status-critical/15 text-status-critical',
            event.severity === 'warning' && 'bg-status-warn/15 text-status-warn',
            event.severity === 'info' && 'bg-surface-2 text-fg-muted',
          )}
        >
          {event.kind.replace(/_/g, ' ')}
        </span>
      </td>
      <td
        className="px-2 py-1"
        style={{
          color:
            event.severity === 'critical'
              ? 'var(--color-status-critical)'
              : event.severity === 'warning'
                ? 'var(--color-status-warn)'
                : 'var(--color-fg-secondary)',
        }}
      >
        {eventSummary(event)}
      </td>
    </tr>
  );
}
